import { useEffect, useRef, useState } from 'react';

import useClientTimers from './use-client-timers';
import { tryGetNativeGlsResponse, useGLS } from './use-gls';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { restartGameTimerMarks } from '@/features/Timer/gameTimerMarks';
import { resetGameTime, setGameWindowContext } from '@/store/actions';
import { computeLaunchJackpotMode } from '@/features/SWJ/helpers/jackpot-mode-launch';
import { areUserSessionTokensReadyForGame } from '@/utilities/helpers/areUserSessionTokensReadyForGame';
import useGameUidParam from '@/lostHooks/gameUid/useGameUid';
import resolveLaunchGameUidFromLocation from '@/lostHooks/gameUid/resolveLaunchGameUidFromLocation';
import useIsGameInMaintenance from '@/features/Maintenance/useIsGameInMaintenance';

const useClientWrapperEffects = () => {
  const [isStuckedLoading, setIsStuckedLoading] = useState(false);

  useClientTimers(setIsStuckedLoading);

  const { userId, authToken, loginToken } = useStore(
    useShallow((s) => ({
      userId: s.userSessionContext.userId,
      authToken: s.userSessionContext.authToken,
      loginToken: s.userSessionContext.loginToken,
    })),
  );

  const { platform, region } = useStore(
    useShallow((s) => ({
      platform: s.runtime.glsPlatform,
      region: s.runtime.region,
    })),
  );

  const userSessionContext = useStore((s) => s.userSessionContext);

  const geolocationToken = useStore(
    useShallow((s) => s.geolocationContext.geolocationToken),
  );
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const gameFetchSuccess = useStore((s) => s.gameStatus.gameFetchSuccess);
  const gameFetchFailed = useStore((s) => s.gameStatus.gameFetchFailed);

  const { fetchGame } = useGLS();

  const isGameInMaintenance = useIsGameInMaintenance();

  const sessionTokensReady =
    areUserSessionTokensReadyForGame(userSessionContext);

  const urlGameUid = useGameUidParam();
  const prevGameResetRevisionRef = useRef(gameResetRevision);

  useEffect(() => {
    if (prevGameResetRevisionRef.current === gameResetRevision) {
      return;
    }

    prevGameResetRevisionRef.current = gameResetRevision;

    if (useStore.getState().gameWindowContext.skipNextGlsReset) {
      setGameWindowContext({ skipNextGlsReset: false });
    }
  }, [gameResetRevision]);

  useEffect(() => {
    // A mid-game geolocation refresh (silent renewal) changes geolocationToken
    // without a reset, which would otherwise re-run this effect and re-launch
    // the game. Every real re-launch path (soft reload, session reset) clears
    // gameFetchSuccess first, so if it's still set the game is already up and a
    // refreshed token must not re-fetch — the handler pushes it to FGP instead.
    if (gameFetchSuccess) {
      return;
    }

    // Terminal GLS business failure (e.g. USER_IS_NOT_ELIGIBLE): blocks
    // keep-alive / UM token object churn from re-fetching. Explicit retries
    // clear gameFetchFailed via soft-reload presets, resetAllGameParts, or
    // resetSessionConsumers before bumping gameResetRevision.
    if (gameFetchFailed) {
      return;
    }

    if (useStore.getState().gameStatus.jackpotReconcileRunning) {
      return;
    }

    // Skip GLS for disabled/invalid games so no launch-error popup is shown.
    if (isGameInMaintenance) {
      return;
    }

    const launchGameUid = resolveLaunchGameUidFromLocation() ?? urlGameUid;
    if (!launchGameUid || !geolocationToken) {
      return;
    }

    const { jwt: authJwt } = authToken ?? {};
    const { jwt: loginJwt } = loginToken ?? {};

    if (!authJwt || !loginJwt || !userId) {
      return;
    }

    // Native GLS pre-fetch applies only to this first-ever launch; the reconcile
    // flows always go through the web fetch (they never pass a prefetched
    // response), so native stays out of reconciliation.
    const nativeResponse = tryGetNativeGlsResponse();

    resetGameTime();
    restartGameTimerMarks();

    void fetchGame(
      {
        userId,
        authToken: authJwt,
        loginToken: loginJwt,
        gameUid: launchGameUid,
        geolocationToken: geolocationToken,
        region: region ?? undefined,
        glsPlatform: platform,
        jackpotMode: computeLaunchJackpotMode(),
      },
      false,
      nativeResponse,
    );
  }, [
    sessionTokensReady,
    userId,
    urlGameUid,
    geolocationToken,
    gameResetRevision,
    gameFetchSuccess,
    gameFetchFailed,
    isGameInMaintenance,
    fetchGame,
    authToken,
    loginToken,
    platform,
    region,
  ]);

  return { isStuckedLoading };
};

export default useClientWrapperEffects;
